import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { baseUrl } from '../../../constants';

export interface MaintenanceStatusDto {
  vehicleId: number;
  vehiclePlate: string;
  model: string;
  totalDistanceKm?: number;
  totalDeliveries: number;
  daysSinceLastMaintenance: number;
  needsMaintenance: boolean;
  lastMaintenanceDate?: string | null;
  lastStartFuel?: number | null;
  lastEndFuel?: number | null;
}

export interface MaintenanceRecordDto {
  id: number;
  vehicleId: number;
  vehiclePlate: string;
  recordType: string;
  description: string;
  cost: number;
  odometer?: number | null;
  serviceDate: string;
}

export interface MaintenanceAnalyticsDto {
  totalRecords: number;
  serviceCount: number;
  repairCount: number;
  totalCost: number;
  thisMonthCost: number;
}

export interface MeterLogDto {
  id: number;
  vehicleId: number;
  plateNumber: string;
  workDate: string;
  startMeter?: number | null;
  endMeter?: number | null;
  distance?: number | null;
  startFuelLevel?: number | null;
  endFuelLevel?: number | null;
  fuelUsed?: number | null;
}

export interface PhoneLogDto {
  id: number;
  riderId: number;
  riderName: string;
  workDate: string;
  startPhoneState?: string | null;
  startCreditAmount?: number | null;
  endCreditAmount?: number | null;
  creditUsed?: number | null;
}

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private baseUrl = `${baseUrl}Maintenance`;

  constructor(private http: HttpClient) {}

  private get headers() {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    });
    if (token) {
      console.log('%c[MaintenanceService] ✅ Token attached', 'color: green');
    } else {
      console.warn('%c[MaintenanceService] ⚠️ No token found', 'color: orange');
    }
    return { headers };
  }

  /** Run maintenance check for all vehicles */
  check(): Observable<MaintenanceStatusDto[]> {
    console.log('%c[MaintenanceService] 🔄 Running maintenance check...', 'color: dodgerblue');
    return this.http.get<MaintenanceStatusDto[]>(`${this.baseUrl}/check`, this.headers).pipe(
      tap({
        next: (res) => console.log('%c[MaintenanceService] ✅ Check result:', 'color: green', res),
        error: (err) => console.error('[MaintenanceService] ❌ Check failed:', err)
      })
    );
  }

  /** Mark a vehicle as maintained */
  markDone(vehicleId: number): Observable<any> {
    console.log('%c[MaintenanceService] 🛠️ Mark as maintained:', 'color: purple', { vehicleId });
  
    return this.http.put(`${this.baseUrl}/${vehicleId}/done`, {}, {
      ...this.headers,
      responseType: 'text'  // ✅ Expect plain text response
    }).pipe(
      tap({
        next: (res) => console.log('%c[MaintenanceService] ✅ Marked as maintained:', 'color: green', res),
        error: (err) => console.error('[MaintenanceService] ❌ Mark done failed:', err)
      })
    );
  }

  getRecords(vehicleId?: number): Observable<MaintenanceRecordDto[]> {
    const query = vehicleId ? `?vehicleId=${vehicleId}` : '';
    return this.http.get<MaintenanceRecordDto[]>(`${this.baseUrl}/records${query}`, this.headers);
  }

  addRecord(payload: {
    vehicleId: number;
    recordType: string;
    description: string;
    cost: number;
    odometer?: number;
    serviceDate?: string;
  }): Observable<MaintenanceRecordDto> {
    return this.http.post<MaintenanceRecordDto>(`${this.baseUrl}/records`, payload, this.headers);
  }

  getAnalytics(): Observable<MaintenanceAnalyticsDto> {
    return this.http.get<MaintenanceAnalyticsDto>(`${this.baseUrl}/analytics`, this.headers);
  }

  getMeterLogs(): Observable<MeterLogDto[]> {
    return this.http.get<MeterLogDto[]>(`${baseUrl}Vehicles/meter-logs?take=200`, this.headers);
  }

  getPhoneLogs(): Observable<PhoneLogDto[]> {
    return this.http.get<PhoneLogDto[]>(`${baseUrl}Riders/phone-logs?take=200`, this.headers);
  }
}
